/**
 * Date comparison helpers.
 *
 * ISO-8601 strings must never be compared lexically: values with different
 * offsets ("2026-06-01T00:00:00-06:00" vs "2026-06-01T05:00:00Z") or precision
 * ("2026-06-01" vs "2026-06-01T10:00:00Z") sort incorrectly as strings. These
 * helpers compare parsed timestamps instead.
 *
 * Date-only values ("YYYY-MM-DD") come from date pickers and represent a whole
 * calendar day in the viewer's local time zone: as a lower bound they mean the
 * start of that day, as an upper bound they mean the end of that day.
 */

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Whether the value is a date-only ISO string ("YYYY-MM-DD"). */
export function isDateOnly(value: string): boolean {
  return DATE_ONLY_PATTERN.test(value.trim());
}

function parseDateOnly(value: string, endOfDay: boolean): number | null {
  const match = DATE_ONLY_PATTERN.exec(value.trim());
  if (!match) return null;
  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  const day = Number(match[3]);
  const date = endOfDay
    ? new Date(year, monthIndex, day, 23, 59, 59, 999)
    : new Date(year, monthIndex, day, 0, 0, 0, 0);
  // Reject overflowed dates such as 2026-02-31.
  if (date.getFullYear() !== year || date.getMonth() !== monthIndex || date.getDate() !== day) {
    return null;
  }
  return date.getTime();
}

/**
 * Parses an ISO-8601 date or date-time into epoch milliseconds. Date-only values
 * resolve to the start of that local day. Returns null for empty/invalid input.
 */
export function parseTimestamp(value: string | null | undefined): number | null {
  if (value == null) return null;
  const trimmed = value.trim();
  if (trimmed === "") return null;
  if (isDateOnly(trimmed)) return parseDateOnly(trimmed, false);
  const time = Date.parse(trimmed);
  return Number.isNaN(time) ? null : time;
}

/** Lower bound of a range: date-only means the start of that day. */
export function rangeStartTimestamp(from: string | null | undefined): number | null {
  return parseTimestamp(from);
}

/** Upper bound of a range: date-only means the end of that day (23:59:59.999). */
export function rangeEndTimestamp(to: string | null | undefined): number | null {
  if (to == null) return null;
  const trimmed = to.trim();
  if (isDateOnly(trimmed)) return parseDateOnly(trimmed, true);
  return parseTimestamp(trimmed);
}

/** Whether the value is empty or a parseable ISO-8601 date / date-time. */
export function isValidDateOrEmpty(value: string | null | undefined): boolean {
  if (value == null || value.trim() === "") return true;
  return parseTimestamp(value) !== null;
}

/**
 * Whether `start` is on or before `end`. Missing or unparseable bounds are not
 * an ordering error (validate the format separately). A date-only `end` covers
 * its whole day, so a same-day start/end pair is valid.
 */
export function isDateRangeOrdered(
  start: string | null | undefined,
  end: string | null | undefined,
): boolean {
  const startTime = rangeStartTimestamp(start);
  const endTime = rangeEndTimestamp(end);
  if (startTime === null || endTime === null) return true;
  return startTime <= endTime;
}

/**
 * Whether `value` falls inside the inclusive range [`from`, `to`]. Missing bounds
 * are open. An unparseable `value` is never inside a bounded range.
 */
export function isWithinDateRange(
  value: string | null | undefined,
  from?: string | null,
  to?: string | null,
): boolean {
  const lower = rangeStartTimestamp(from);
  const upper = rangeEndTimestamp(to);
  if (lower === null && upper === null) return true;
  const time = parseTimestamp(value);
  if (time === null) return false;
  if (lower !== null && time < lower) return false;
  if (upper !== null && time > upper) return false;
  return true;
}
